import '@fontsource/poppins';
import { Icon } from '@iconify/react';
import axios from 'axios';
import image from 'src/assets/images/rectangle.png';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import Paper from '@mui/material/Paper';

import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { DeleteButton, ViewButton } from '@src/components/buttons';
import { useAuth } from '@src/contexts/authContext';

import { openLinkInPopUpWindow } from '@utils';

const urls = `${process.env.REACT_APP_API_URL}/event/get-event`;

export default function TableEventListed() {
  const [listEvents, setListEvents] = useState([]);
  const format_date = { year: 'numeric', month: 'short', day: 'numeric' };
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [loading, setLoading] = useState();
  const [deleteStatus, setDeleteStatus] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const getInitialPage = () => {
    const storedPage = sessionStorage.getItem('eventListedPage');
    return storedPage ? parseInt(storedPage, 10) : 0;
  };
  const getInitialRowsPerPage = () => {
    const storedRows = sessionStorage.getItem('eventListedRowsPerPage');
    return storedRows ? parseInt(storedRows, 10) : 10;
  };

  const [page, setPage] = React.useState(getInitialPage());
  const [rowsPerPage, setRowsPerPage] = React.useState(getInitialRowsPerPage());

  const [searchQuery, setSearchQuery] = useState('');
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleUpdateEvent = (eventId) => {
    sessionStorage.setItem('eventListedPage', page);
    sessionStorage.setItem('eventListedRowsPerPage', rowsPerPage);
    navigate(`/update-listed-event/${eventId}`);
  };

  const [sortOrder, setSortOrder] = useState('asc');
  const [sortedList, setSortedList] = useState([]);

  useEffect(() => {
    const filteredList = listEvents?.filter((row) => {
      return (
        row?.event_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (row?.roles &&
          row.roles.some((role) =>
            role.toLowerCase().includes(searchQuery.toLowerCase()),
          )) ||
        row?.event_date.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row?.organiser_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
    const sortedData = [...filteredList].sort((a, b) => {
      const dateA = new Date(a.event_date).getTime();
      const dateB = new Date(b.event_date).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setSortedList(sortedData);
  }, [listEvents, searchQuery, sortOrder]);

  const handleSortToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenSlide = (event_link, event_title) => async () =>
    openLinkInPopUpWindow(event_link, event_title);

  const extractHostName = (url) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname;
    } catch (error) {
      console.error('url');
      return 'unknown link';
    }
  };

  const [status, setStatus] = useState('listed');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${urls}?event_status=${status}`, {
          headers: {
            Authorization: `Bearer ${auth?.idToken}`,
          },
        });

        setListEvents(response.data);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log('Error', error.message);
      }
    };
    if (auth?.idToken) {
      fetchData();
    }
  }, [auth, deleteStatus, status]);

  const formatTime = (time) => {
    return new Date(time).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/event/${id}`,
        {
          headers: {
            Authorization: `Bearer ${auth.idToken}`,
            'My-Custom-Header': 'foobar',
          },
        },
      );
      setDeleteStatus('Delete successful');
      setListEvents(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmationDialogClose = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmationDialogConfirm = () => {
    handleDelete(deleteId);
    setShowDeleteConfirmation(false);
  };

  const handleEventReport = (eventId) => {
    navigate(`/event-report/${eventId}`);
  };

  useEffect(() => {
    sessionStorage.setItem('eventListedPage', page);
  }, [page]);
  useEffect(() => {
    sessionStorage.setItem('eventListedRowsPerPage', rowsPerPage);
  }, [rowsPerPage]);

  return (
    <>
      <Box mb={1} sx={{ width: '100%' }} className='flex justify-end px-4 py-4'>
        <TextField
          fullWidth
          size='medium'
          variant='outlined'
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              height: '50px',
            },
            mt: '5px',
            width: '250px',
          }}
          onChange={handleSearch}
          placeholder='Search by title, role or date'
        />
      </Box>
      {loading ? (
        <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
      ) : (
        <span className='flex  flex-col items-start'>
          <TableContainer
            className='rounded-xl'
            component={Paper}
            style={{
              width: '100%',
              paddingBottom: '12px',
              paddingRight: '12px',
              backgroundColor: '#ffffff',
              borderColor: 'ffffff',
            }}
          >
            <Table
              sx={{
                minWidth: 350,
                backgroundColor: '#ffffff',
                borderColor: 'ffffff',
              }}
              aria-label='simple table'
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ width: '60px', border: 'gray' }}
                  >
                    Event Image
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '320px', border: 'gray' }}
                  >
                    Event Title
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '200px', border: 'gray' }}
                  >
                    Organiser Name
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '200px', border: 'gray' }}
                  >
                    <TableSortLabel
                      active
                      direction={sortOrder}
                      onClick={handleSortToggle}
                      className='text-xs font-bold text-gray-400'
                      sx={{ width: '50px' }}
                    >
                      Event Date/Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '230px', border: 'gray' }}
                  >
                    Location
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '170px', border: 'gray' }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    className='text-xs  font-bold text-gray-400'
                    style={{ padding: 4, width: '200px', border: 'gray' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(sortedList) && sortedList.length > 0 ? (
                  sortedList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        hover
                        key={row?._id}
                        tabIndex={-1}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell
                          component='th'
                          scope='row'
                          className='border-white py-5'
                        >
                          <img
                            src={row?.event_image ? row?.event_image : image}
                            alt='event'
                            width='59px'
                            height='32px'
                          />
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{
                            // padding: 4,
                            margin: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row?.event_title}
                        </TableCell>
                        <TableCell
                          className='border-white  text-sm font-bold text-gray-500'
                          style={{ padding: 4 }}
                        >
                          {row?.organiser_name}
                        </TableCell>
                        <TableCell
                          style={{ padding: 4 }}
                          className='border-white'
                        >
                          <div className='flex justify-start text-sm font-bold text-gray-500'>
                            {new Date(row?.event_date).toLocaleDateString(
                              'au-AU',
                              format_date,
                            )}
                            <br />
                            {formatTime(row?.event_from)} -{' '}
                            {formatTime(row?.event_to)}
                          </div>
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 4 }}
                        >
                          {(row?.unit_number ||
                            row?.street ||
                            row?.city ||
                            row?.country ||
                            row?.postal_code) &&
                          row?.live_stream ? (
                            <div>Hybrid event</div>
                          ) : row?.unit_number ||
                            row?.street ||
                            row?.city ||
                            row?.country ||
                            row?.postal_code ? (
                            `${row?.unit_number}, ${row?.street}, ${row?.city}, ${row?.country}, ${row?.postal_code}`
                          ) : row?.live_stream ? (
                            <div>
                              Online at:{' '}
                              <Button
                                variant='text'
                                className='justify-left flex text-sm font-bold text-cyan-600 underline'
                                sx={{ textTransform: 'none', padding: 0 }}
                                onClick={() =>
                                  handleOpenSlide(row.live_stream || '')
                                }
                              >
                                {extractHostName(row.live_stream)}
                              </Button>
                            </div>
                          ) : (
                            'No location added'
                          )}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 4, margin: 2 }}
                        >
                          {row?.roles && row.roles.length > 0
                            ? (() => {
                                const firstRole = row.roles[0]
                                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                                  .replace(/^[a-z]/, (c) => c.toUpperCase())
                                  .replace(
                                    /([._-])([a-zA-Z])/g,
                                    (m, sep, chr) => ` ${chr.toUpperCase()}`,
                                  );
                                return row.roles.length > 1
                                  ? `${firstRole}, etc.`
                                  : firstRole;
                              })()
                            : 'No role'}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-cyan-600'
                          style={{ padding: 0 }}
                        >
                          <div className='flex'>
                            <ViewButton
                              onClick={() => handleUpdateEvent(row?._id)}
                            />
                            &nbsp; &nbsp;
                            <DeleteButton
                              onClick={() =>
                                showDeleteConfirmationDialog(row?._id)
                              }
                            />
                            &nbsp; &nbsp;
                            <Icon
                              icon='fluent:info-24-regular'
                              color='#1788b9'
                              width='26'
                              onClick={() => handleEventReport(row?._id)}
                            />
                          </div>
                          {showDeleteConfirmation && (
                            <Dialog
                              open={showDeleteConfirmation}
                              onClose={handleConfirmationDialogClose}
                            >
                              <DialogTitle>Confirm Delete</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Are you sure you want to delete this event?
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button
                                  onClick={handleConfirmationDialogClose}
                                  color='primary'
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleConfirmationDialogConfirm}
                                  color='error'
                                >
                                  Delete
                                </Button>
                              </DialogActions>
                            </Dialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      className='border border-white'
                      colSpan={6}
                      align='center'
                    >
                      <div className='font-poppins py-6 text-gray-500'>
                        No events listed
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box
              className='flex justify-end py-3'
              sx={{ width: '100%', fontFamily: 'Poppins' }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component='div'
                count={listEvents.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </TableContainer>
        </span>
      )}
    </>
  );
}
